// packages/web-ui/src/components/inline-cards/FileUploadConfirm.tsx
//
// Story W2-S6 AC #3 — inline FileUploadConfirm card.
//
// SCOPE: presentational. The actual upload network call is owned by the
// composer (W2-S7) — this card just renders the warning, file info,
// Continue/Cancel buttons, and signals via callbacks.
//
// Lit precedent: packages/webview-ui/src/panels/chat-panel/components/file-upload-confirm.ts
//   - Cancel auto-focused on mount (line 39–41)
//   - Tab cycles between Continue and Cancel only (lines 94–108)
//   - Warning text wording — verbatim match (line 55)

import { useCallback, useEffect, useRef, useState } from 'react';
import type { JSX, KeyboardEvent } from 'react';
import { AlertTriangle } from 'lucide-react';
import { CardShell } from './CardShell.js';
import type { FileUploadConfirmEnvelopeData } from '../../state/inline-cards.js';

export interface FileUploadConfirmProps extends FileUploadConfirmEnvelopeData {
  /**
   * Performs the upload. Returns the wire response on success or an
   * error message string on failure. The composer is the producer
   * (W2-S7); this story owns only the card.
   */
  onConfirm: (
    uploadHandle: string,
  ) => Promise<
    | { ok: true; result: { uploadId: string; contentType: string; size: number } }
    | { ok: false; message: string }
  >;
  /**
   * Called when the user clicks Cancel. The composer is responsible
   * for removing the staged file reference.
   */
  onCancel: () => void;
  /**
   * Called on a successful upload with the wire response payload. The
   * composer attaches `uploadId` to the next outgoing chat message.
   */
  onConfirmed: (result: {
    uploadId: string;
    contentType: string;
    size: number;
  }) => void;
}

/**
 * Same algorithm as Lit precedent
 * (packages/webview-ui/src/panels/chat-panel/components/file-upload-confirm.ts:9-13).
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const UPLOAD_WARNING_TEXT =
  'File content will be sent to the AI provider for processing.';

const WARNING_ELEMENT_ID = 'file-upload-confirm-warning-text';

export function FileUploadConfirm(props: FileUploadConfirmProps): JSX.Element {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const continueRef = useRef<HTMLButtonElement>(null);
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Cancel auto-focused on mount (Lit precedent — safe default).
  useEffect(() => {
    const handle = window.setTimeout(() => {
      cancelRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(handle);
  }, []);

  const handleConfirm = useCallback(async () => {
    setBusy(true);
    setErrorMessage(null);
    const result = await props.onConfirm(props.uploadHandle);
    setBusy(false);
    if (result.ok) {
      props.onConfirmed(result.result);
    } else {
      setErrorMessage(result.message);
    }
  }, [props]);

  // Focus trap — Tab cycles between Continue and Cancel only.
  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab') return;
    const order = [continueRef.current, cancelRef.current].filter(
      (b): b is HTMLButtonElement => b !== null,
    );
    if (order.length === 0) return;
    const current = order.indexOf(document.activeElement as HTMLButtonElement);
    const direction = event.shiftKey ? -1 : 1;
    const startIndex = current < 0 ? 0 : current;
    const next = (startIndex + direction + order.length) % order.length;
    event.preventDefault();
    order[next]?.focus();
  }, []);

  return (
    <div onKeyDown={handleKeyDown}>
      <CardShell
        role="alertdialog"
        ariaLabel="File upload confirmation"
        ariaDescribedBy={WARNING_ELEMENT_ID}
        cardKind="file-upload-confirm"
        testId="file-upload-confirm-card"
      >
        <div
          className="flex items-start gap-2"
          data-testid="file-upload-confirm-warning"
        >
          <AlertTriangle
            size={16}
            className="mt-0.5 text-accent-amber"
            aria-hidden="true"
          />
          <span id={WARNING_ELEMENT_ID} className="text-sm text-fg">
            {UPLOAD_WARNING_TEXT}
          </span>
        </div>

        <div className="mt-3 flex items-center justify-between gap-2">
          <div className="text-sm text-fg" data-testid="file-upload-confirm-info">
            {props.fileName} — {formatFileSize(props.fileSize)}
          </div>
          <span
            className="px-2 py-0.5 text-xs rounded-full bg-cream-100 text-taupe-700"
            data-testid="file-upload-confirm-chip"
          >
            {props.contentType !== undefined && props.contentType.length > 0
              ? props.contentType
              : 'application/octet-stream'}
          </span>
        </div>

        {errorMessage !== null ? (
          <div
            role="alert"
            data-testid="file-upload-confirm-error"
            className="mt-2 text-sm text-accent-red"
          >
            Upload failed: {errorMessage}.{' '}
            <button
              type="button"
              data-testid="file-upload-confirm-retry"
              onClick={() => void handleConfirm()}
              className="underline"
            >
              Retry
            </button>{' '}
            <button
              type="button"
              data-testid="file-upload-confirm-error-cancel"
              onClick={props.onCancel}
              className="underline"
            >
              Cancel
            </button>
          </div>
        ) : null}

        <div className="mt-3 flex justify-end gap-2">
          <button
            type="button"
            ref={cancelRef}
            data-testid="file-upload-confirm-cancel"
            aria-label="Cancel file upload"
            onClick={props.onCancel}
            disabled={busy}
            className="px-3 py-1.5 rounded-none border border-taupe-500 hover:border-taupe-900 text-fg text-xs font-semibold uppercase tracking-wide disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            ref={continueRef}
            data-testid="file-upload-confirm-continue"
            aria-label="Continue uploading file"
            onClick={() => void handleConfirm()}
            disabled={busy}
            className="px-3 py-1.5 rounded-none bg-taupe-900 hover:bg-audi-red text-white text-xs font-semibold uppercase tracking-wide disabled:opacity-50"
          >
            Continue
          </button>
        </div>
      </CardShell>
    </div>
  );
}
